import type { FC } from 'react';
import { useRef } from 'react';

import { SWReactIcons } from '@similarweb/icons';

import { useAppState } from '../../AppStateProvider';
import type { Company } from '../../domain/company';
import { useIsWithinCrm } from '../../hooks/useIsWithinCrm';
import { useIsWithinLinkedin } from '../../hooks/useIsWithinLinkedin';
import { useIsWithinTools } from '../../hooks/useIsWithinTools';
import { AppMode } from '../../services/appStore';
import { getFaviconUrl } from '../../services/favicon';
import { translate } from '../../services/i18n';
import { Tooltip } from '../Tooltip';

import {
  StyledCompanyIconTooltip,
  StyledCompanyLogo,
  StyledCompanyName,
  StyledCompanyNameContainer,
  StyledCompanyWebsiteIcon,
} from './styles';
import { useCompanyNameTracking } from './useCompanyNameTracking';

export type CompanyNameVariant = 'overview' | 'contacts_list';

type CompanyNameProps = {
  company: Company | undefined;
  variant?: CompanyNameVariant;
};

export const CompanyName: FC<CompanyNameProps> = ({ company, variant = 'overview' }) => {
  const { mode } = useAppState();
  const isWithinLinkedin = useIsWithinLinkedin();
  const { isWithinTools } = useIsWithinTools();
  const isWithinCrm = useIsWithinCrm();
  const namesRef = useRef<HTMLAnchorElement>(null);

  const { handleTrackWebsiteClick, handleTrackLinkedinLinkClick, handleTrackCompanyOverviewClick } =
    useCompanyNameTracking(company);

  if (!company) {
    return null;
  }

  const lineHeight = variant === 'contacts_list' ? 18 : 24;

  const isMoreThanOneRow = namesRef?.current?.clientHeight ? namesRef.current.clientHeight / lineHeight > 1 : false;

  const companyName = company.name || company.domain;

  return (
    <StyledCompanyNameContainer variant={variant} data-automation="CompanyNameContainer">
      <StyledCompanyLogo
        src={getFaviconUrl(company.domain)}
        width="24px"
        height="24px"
        alt={company.name}
        isMoreThanOneRow={isMoreThanOneRow}
      />
      <Tooltip
        content={translate('company.overview.name.tooltip')}
        placement="top"
        minWidth={companyName.length > 10 ? 180 : 130}
      >
        <StyledCompanyName
          ref={namesRef}
          data-automation="CompanyName"
          href={`https://pro.similarweb.com/#/sales/account-overview/${company.domain}/company`}
          target="_blank"
          rel="noreferrer"
          lineHeight={lineHeight}
          onMouseDown={handleTrackCompanyOverviewClick}
        >
          {companyName}
        </StyledCompanyName>
      </Tooltip>
      {isWithinLinkedin ||
      isWithinTools ||
      isWithinCrm ||
      mode === AppMode.ProSidebar ||
      mode === AppMode.Salesforce ||
      mode === AppMode.Standalone ? (
        <StyledCompanyIconTooltip
          content={translate('company.overview.website.tooltip')}
          placement="top"
          minWidth={130}
          isMoreThanOneRow={isMoreThanOneRow}
        >
          <a href={`https://${company.domain}`} target="_blank" rel="noreferrer" onMouseDown={handleTrackWebsiteClick}>
            <StyledCompanyWebsiteIcon iconName="link-out" size="xs" />
          </a>
        </StyledCompanyIconTooltip>
      ) : null}
      {!isWithinLinkedin && company.linkedinId ? (
        <StyledCompanyIconTooltip
          content={translate('company.overview.linkedin.tooltip')}
          placement="top"
          minWidth={130}
          isMoreThanOneRow={isMoreThanOneRow}
        >
          <a
            href={`https://linkedin.com/company/${company.linkedinId}`}
            target="_blank"
            rel="noreferrer"
            onMouseDown={handleTrackLinkedinLinkClick}
          >
            <SWReactIcons iconName="linkedin-circle-blue" size="xs" />
          </a>
        </StyledCompanyIconTooltip>
      ) : null}
    </StyledCompanyNameContainer>
  );
};
